import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';

interface UIState {
  theme: Theme;
  sidebarCollapsed: boolean;
  sidebarOpenGroups: string[];
  setTheme: (t: Theme) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (v: boolean) => void;
  toggleSidebarGroup: (id: string) => void;
  setSidebarOpenGroups: (ids: string[]) => void;
  openSidebarGroup: (id: string) => void;
  applyTheme: () => void;
}

function resolveTheme(t: Theme): 'light' | 'dark' {
  if (t === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return t;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      sidebarCollapsed: false,
      sidebarOpenGroups: [],

      setTheme: (theme) => {
        set({ theme });
        const resolved = resolveTheme(theme);
        document.documentElement.classList.toggle('dark', resolved === 'dark');
      },
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),
      toggleSidebarGroup: (id) =>
        set((s) => ({
          sidebarOpenGroups: s.sidebarOpenGroups.includes(id)
            ? s.sidebarOpenGroups.filter((g) => g !== id)
            : [...s.sidebarOpenGroups, id],
        })),
      setSidebarOpenGroups: (ids) => set({ sidebarOpenGroups: ids }),
      openSidebarGroup: (id) =>
        set((s) =>
          s.sidebarOpenGroups.includes(id)
            ? s
            : { sidebarOpenGroups: [...s.sidebarOpenGroups, id] },
        ),
      applyTheme: () => {
        const resolved = resolveTheme(get().theme);
        document.documentElement.classList.toggle('dark', resolved === 'dark');
      },
    }),
    {
      name: 'timmy-ui',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
